import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, RefreshCw, Trash2 } from 'lucide-react';
import { Badge, Button, Callout, PageHeader, Section, Skeleton, useToast } from '@zentera/ui';
import { api, ApiError } from '@zentera/web-core';
import { usePageTitle } from '../components/shell';
import { useCan } from '../lib/auth';

interface IntegrationStatus { enabled: boolean; updatedAt: string | null; endpoint: string }

export function IntegrationPage() {
  usePageTitle('Integração do agente');
  const can = useCan();
  const qc = useQueryClient();
  const toast = useToast();
  const [newToken, setNewToken] = useState('');
  const status = useQuery({ queryKey: ['channel-integration'], queryFn: () => api.get<IntegrationStatus>('/api/integration') });
  const rotate = useMutation({
    mutationFn: () => api.post<{ token: string }>('/api/integration/token'),
    onSuccess: ({ token }) => {
      setNewToken(token);
      void qc.invalidateQueries({ queryKey: ['channel-integration'] });
      toast({ tone: 'success', title: 'Nova credencial criada', description: 'Copie agora; ela não será exibida novamente.' });
    },
    onError: (e) => toast({ tone: 'danger', title: 'Não foi possível criar a credencial', description: e instanceof ApiError ? e.message : undefined }),
  });
  const disable = useMutation({
    mutationFn: () => api.del('/api/integration/token'),
    onSuccess: () => {
      setNewToken('');
      void qc.invalidateQueries({ queryKey: ['channel-integration'] });
      toast({ tone: 'success', title: 'Integração desativada' });
    },
    onError: (e) => toast({ tone: 'danger', title: 'Não foi possível desativar', description: e instanceof ApiError ? e.message : undefined }),
  });

  if (status.isError) return <Callout tone="danger" title="Não foi possível carregar as configurações">Atualize a página e tente novamente.</Callout>;
  if (!status.data) return <Skeleton className="h-96 rounded-lg" />;
  const s = status.data;
  const endpoint = `${window.location.origin}${s.endpoint}`;

  return (
    <div className="space-y-8">
      <PageHeader title="Integração do agente" description="Conecte o agente ao sistema de atendimento que seu provedor já utiliza. O contrato é genérico: o seu sistema envia cada mensagem e recebe a resposta do agente." />
      <Section title="Conexão" description="A credencial identifica esta instalação. Guarde-a no cofre de segredos do seu sistema de atendimento.">
        <div className="mb-4 flex items-center gap-2">
          <Badge tone={s.enabled ? 'success' : 'neutral'}>{s.enabled ? 'Ativa' : 'Não configurada'}</Badge>
          {s.updatedAt && <span className="text-xs text-fg-3">Credencial atualizada em {new Date(s.updatedAt).toLocaleString()}</span>}
        </div>
        {newToken && (
          <Callout tone="warning" title="Copie e armazene esta credencial agora">
            <p>Ela só aparece nesta tela enquanto estiver aberta e não pode ser recuperada depois.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <code className="min-w-0 flex-1 break-all rounded-md bg-bg px-3 py-2 text-xs">{newToken}</code>
              <Button size="sm" icon={<Copy />} onClick={() => void navigator.clipboard.writeText(newToken)}>Copiar</Button>
              <Button size="sm" variant="secondary" icon={<Check />} onClick={() => setNewToken('')}>Já guardei</Button>
            </div>
          </Callout>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {can('settings.write') && <Button icon={s.enabled ? <RefreshCw /> : <KeyRound />} onClick={() => { if (!s.enabled || window.confirm('A credencial atual será invalidada imediatamente. Confirma a rotação?')) rotate.mutate(); }} disabled={rotate.isPending}>{s.enabled ? 'Rotacionar credencial' : 'Gerar credencial'}</Button>}
          {s.enabled && can('settings.write') && <Button variant="secondary" icon={<Trash2 />} onClick={() => { if (window.confirm('A integração será desativada e as chamadas externas passarão a falhar. Confirma?')) disable.mutate(); }} disabled={disable.isPending}>Desativar</Button>}
        </div>
      </Section>

      <Section title="Contrato da API" description="Cada mensagem deve ter um identificador estável. Reenvios com o mesmo message_id não executam o agente outra vez.">
        <div className="space-y-4">
          <div><div className="mb-1 text-xs font-medium text-fg-3">Endpoint</div><code className="text-sm">POST {endpoint}</code></div>
          <div><div className="mb-1 text-xs font-medium text-fg-3">Autenticação</div><code className="text-sm">Authorization: Bearer &lt;credencial&gt;</code></div>
          <pre className="overflow-x-auto rounded-lg border border-line bg-subtle p-4 text-xs leading-relaxed">{`{
  "conversation_id": "id-estavel-no-seu-atendimento",
  "message_id": "id-unico-da-mensagem",
  "sender": "customer",
  "contact": { "id": "id-do-cliente", "phone": "+5511999999999" },
  "text": "Olá, quero consultar minha fatura"
}`}</pre>
          <p className="text-sm text-fg-2">A resposta contém <code>reply</code>, <code>conversation_id</code> da Zentera e <code>handoff</code>. Quando <code>handoff</code> for verdadeiro, encaminhe a conversa a um atendente no seu sistema. Mensagens do atendente podem ser enviadas com <code>sender: "agent"</code>; use <code>resume_automation: true</code> quando devolver a conversa ao agente.</p>
          <p className="text-sm text-fg-2">
            Na transferência, a resposta traz também <code>whisper</code> (sussurro): texto pronto em <code>whisper.text</code> e os mesmos dados estruturados (motivo, se foi transferência silenciosa por falha da IA, cliente, o que a IA fez, últimas mensagens e atendimentos anteriores).
            Mostre-o ao atendente como nota interna; ele nunca deve ser enviado ao cliente.
          </p>
          <Callout tone="neutral" title="Canal independente">O seu sistema continua responsável por entregar mensagens ao cliente. Isso permite começar pelo atendimento que você já usa e também criar um adaptador de WhatsApp oficial depois, usando o mesmo agente.</Callout>
        </div>
      </Section>
    </div>
  );
}
